import React, { useState, useEffect, useCallback } from 'react';
import {
  DollarSign,
  Crown,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
  Clock,
  RefreshCw,
  Search,
  Check,
  X,
  Plus,
  Lock,
  Unlock,
  CreditCard,
  Building2,
  ExternalLink,
  ChevronRight,
  TrendingUp,
  Sliders,
  Calendar,
  Zap,
  Users,
  AlertTriangle,
  Receipt,
  Eye,
  CheckCircle,
  XCircle,
  FileText,
  Phone,
  Bot,
  PhoneCall,
  BookOpen,
  Sparkles,
  Calculator
} from 'lucide-react';

interface TenantBillingHubProps {
  authHeader?: string | (() => Record<string, string>);
  tenants?: any[];
  onRefreshTenants?: () => void;
}

export const TenantBillingHub: React.FC<TenantBillingHubProps> = ({
  authHeader,
  tenants = [],
  onRefreshTenants
}) => {
  const [activeTab, setActiveTab] = useState<'tenants' | 'token-costs' | 'payments' | 'slips'>('tenants');
  const [payments, setPayments] = useState<any[]>([]);
  const [loadingPayments, setLoadingPayments] = useState<boolean>(false);
  const [slips, setSlips] = useState<any[]>([]);
  const [loadingSlips, setLoadingSlips] = useState<boolean>(false);
  const [slipActionLoading, setSlipActionLoading] = useState<string | null>(null);
  const [selectedSlipImage, setSelectedSlipImage] = useState<string | null>(null);
  const [rejectModalTenant, setRejectModalTenant] = useState<any | null>(null);
  const [rejectReason, setRejectReason] = useState<string>('');

  // AI Token Cost Intelligence States
  const [costData, setCostData] = useState<any | null>(null);
  const [loadingCosts, setLoadingCosts] = useState<boolean>(false);
  const [costRateInput, setCostRateInput] = useState<number>(305);
  const [costSearchQuery, setCostSearchQuery] = useState<string>('');
  const [costSortBy, setCostSortBy] = useState<'cost' | 'tokens' | 'name'>('cost');

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [filterType, setFilterType] = useState<'all' | 'trial' | 'pro' | 'whitelabel' | 'suspended'>('all');

  // Modal for editing tenant subscription
  const [selectedTenant, setSelectedTenant] = useState<any | null>(null);
  const [subActionLoading, setSubActionLoading] = useState<boolean>(false);
  const [modalFeedback, setModalFeedback] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  // Edit form states
  const [editPlan, setEditPlan] = useState<'basic_trial' | 'pro' | 'enterprise'>('basic_trial');
  const [editWhiteLabel, setEditWhiteLabel] = useState<boolean>(false);
  const [editTokensToAdd, setEditTokensToAdd] = useState<number>(0);
  const [editDaysToAdd, setEditDaysToAdd] = useState<number>(0);
  const [editStatus, setEditStatus] = useState<'active' | 'suspended'>('active');

  const getHeaders = useCallback((): Record<string, string> => {
    if (typeof authHeader === 'function') {
      return authHeader();
    }
    if (typeof authHeader === 'string') {
      return { 'Authorization': authHeader, 'Content-Type': 'application/json' };
    }
    return { 'Content-Type': 'application/json' };
  }, [authHeader]);

  const fetchPayments = useCallback(async () => {
    setLoadingPayments(true);
    try {
      const res = await fetch('/api/admin/tenants/payments', {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setPayments(Array.isArray(data) ? data : []);
      }
    } catch (err) {
      console.warn('[TenantBillingHub] Payments fetch error:', err);
    } finally {
      setLoadingPayments(false);
    }
  }, [getHeaders]);

  const fetchSlips = useCallback(async () => {
    setLoadingSlips(true);
    try {
      const res = await fetch('/api/tenet/admin/billing/slips', {
        headers: getHeaders()
      });
      if (res.ok) {
        const data = await res.json();
        setSlips(Array.isArray(data.pendingSlips) ? data.pendingSlips : []);
      }
    } catch (err) {
      console.warn('[TenantBillingHub] Slips fetch error:', err);
    } finally {
      setLoadingSlips(false);
    }
  }, [getHeaders]);

  const fetchTokenCosts = useCallback(async (customRate?: number) => {
    setLoadingCosts(true);
    try {
      const rateToUse = customRate ?? costRateInput;
      const res = await fetch(`/api/admin/tenants/token-costs?rate=${rateToUse}`, {
        headers: getHeaders(),
      });
      const data = await res.json();
      if (data.success) {
        setCostData(data);
        if (data.usdToLkrRate) setCostRateInput(data.usdToLkrRate);
      }
    } catch (err) {
      console.warn('[TenantBillingHub] Cost fetch error:', err);
    } finally {
      setLoadingCosts(false);
    }
  }, [costRateInput, getHeaders]);

  useEffect(() => {
    fetchPayments();
    fetchSlips();
    fetchTokenCosts();
  }, [fetchPayments, fetchSlips, fetchTokenCosts]);

  const handleApproveSlip = async (tenantId: number, planName: string, tenantName: string) => {
    if (!window.confirm(`Approve Bank Slip for "${tenantName}"?\n\nThis will immediately activate ${planName}, credit their ledger, and unlock Pro features.`)) {
      return;
    }
    setSlipActionLoading(String(tenantId));
    try {
      const res = await fetch(`/api/tenet/admin/billing/slips/${tenantId}/approve`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({})
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to approve');
      alert(`✅ Success: ${data.message}`);
      if (onRefreshTenants) onRefreshTenants();
      fetchPayments();
      fetchSlips();
    } catch (err: any) {
      alert(`❌ Error approving slip: ${err.message}`);
    } finally {
      setSlipActionLoading(null);
    }
  };

  const handleRejectSlip = async () => {
    if (!rejectModalTenant) return;
    setSlipActionLoading(String(rejectModalTenant.tenantId));
    try {
      const res = await fetch(`/api/tenet/admin/billing/slips/${rejectModalTenant.tenantId}/reject`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ reason: rejectReason })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to reject');
      alert(`Slip marked as rejected. Tenant will be notified to re-upload.`);
      setRejectModalTenant(null);
      setRejectReason('');
      if (onRefreshTenants) onRefreshTenants();
      fetchSlips();
    } catch (err: any) {
      alert(`❌ Error rejecting slip: ${err.message}`);
    } finally {
      setSlipActionLoading(null);
    }
  };

  const openEditModal = (t: any) => {
    setSelectedTenant(t);
    setEditPlan(t.planType || 'basic_trial');
    setEditWhiteLabel(t.isWhiteLabelEnabled === true);
    setEditTokensToAdd(0);
    setEditDaysToAdd(0);
    setEditStatus(t.status === 'suspended' ? 'suspended' : 'active');
    setModalFeedback(null);
  };

  const handleSaveSubscription = async () => {
    if (!selectedTenant) return;
    setSubActionLoading(true);
    setModalFeedback(null);
    try {
      const payload: any = {
        planType: editPlan,
        isWhiteLabelEnabled: editWhiteLabel,
        status: editStatus
      };
      if (editTokensToAdd !== 0) payload.tokensToAdd = editTokensToAdd;
      if (editDaysToAdd > 0) payload.additionalDays = editDaysToAdd;

      const res = await fetch(`/api/admin/tenants/${selectedTenant.id}/subscription`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to update subscription');
      }

      setModalFeedback({ type: 'success', message: 'Subscription successfully updated!' });
      if (onRefreshTenants) onRefreshTenants();
      fetchPayments();
      setTimeout(() => {
        setSelectedTenant(null);
      }, 1200);
    } catch (err: any) {
      setModalFeedback({ type: 'error', message: err.message || 'Update failed' });
    } finally {
      setSubActionLoading(false);
    }
  };

  const handleQuickToggleWhiteLabel = async (t: any) => {
    try {
      const nextVal = !t.isWhiteLabelEnabled;
      const res = await fetch(`/api/admin/tenants/${t.id}/subscription`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ isWhiteLabelEnabled: nextVal })
      });
      if (res.ok) {
        if (onRefreshTenants) onRefreshTenants();
      }
    } catch (err) {
      console.warn('Quick toggle error:', err);
    }
  };

  const handleQuickToggleSuspend = async (t: any) => {
    const nextStatus = t.status === 'suspended' ? 'active' : 'suspended';
    const confirmMsg = nextStatus === 'suspended'
      ? `Suspend tenant "${t.name}"? Their agent will stop taking calls and web traffic.`
      : `Re-activate tenant "${t.name}"?`;
    if (!window.confirm(confirmMsg)) return;

    try {
      const res = await fetch(`/api/admin/tenants/${t.id}/subscription`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ status: nextStatus })
      });
      if (res.ok) {
        if (onRefreshTenants) onRefreshTenants();
      }
    } catch (err) {
      console.warn('Suspend toggle error:', err);
    }
  };

  const handleGrantFullBundle = async (t: any) => {
    if (!window.confirm(`Grant Full Pro Bundle to "${t.name}"?\n\n• Setup Fee Marked as Paid\n• White-Label Rebranding Unlocked\n• Unlimited Agent Tokens\n• Next Monthly Maintenance Scheduled (+30 Days)`)) {
      return;
    }
    try {
      const res = await fetch(`/api/admin/tenants/${t.id}/subscription`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({
          planType: 'pro',
          isWhiteLabelEnabled: true,
          additionalDays: 30,
          status: 'active'
        })
      });
      if (res.ok) {
        if (onRefreshTenants) onRefreshTenants();
        fetchPayments();
      }
    } catch (err) {
      console.warn('Grant full bundle error:', err);
    }
  };

  // Metrics calculation
  const totalRevenueUsd = payments.reduce((acc, p) => {
    if (p.status === 'completed') {
      const amt = Number(p.amountUsd) || Number(p.amount) || 0;
      return acc + amt;
    }
    return acc;
  }, 0);

  const proCount = tenants.filter(t => t.planType === 'pro' || t.planType === 'enterprise').length;
  const trialCount = tenants.filter(t => !t.planType || t.planType === 'basic_trial').length;
  const whiteLabelCount = tenants.filter(t => t.isWhiteLabelEnabled === true).length;
  const suspendedCount = tenants.filter(t => t.status === 'suspended').length;

  // Filtered tenants
  const filteredTenants = tenants.filter(t => {
    const q = searchQuery.toLowerCase();
    const matchQuery = !q ||
      t.name?.toLowerCase().includes(q) ||
      t.apiKey?.toLowerCase().includes(q) ||
      t.adminEmail?.toLowerCase().includes(q) ||
      t.url?.toLowerCase().includes(q);

    if (!matchQuery) return false;

    if (filterType === 'trial') return !t.planType || t.planType === 'basic_trial';
    if (filterType === 'pro') return t.planType === 'pro' || t.planType === 'enterprise';
    if (filterType === 'whitelabel') return t.isWhiteLabelEnabled === true;
    if (filterType === 'suspended') return t.status === 'suspended';
    return true;
  });

  // Filtered payments
  const filteredPayments = payments.filter(p => {
    const q = searchQuery.toLowerCase();
    return !q ||
      p.tenantName?.toLowerCase().includes(q) ||
      p.orderId?.toLowerCase().includes(q) ||
      p.planType?.toLowerCase().includes(q) ||
      p.currency?.toLowerCase().includes(q);
  });

  const pendingSlipsCount = slips.filter(s => s.slip?.status === 'pending_verification' || s.slip?.status === 'pending').length;

  // Filtered slips
  const filteredSlips = slips.filter(s => {
    const q = searchQuery.toLowerCase();
    const slip = s.slip || {};
    return !q ||
      s.tenantName?.toLowerCase().includes(q) ||
      s.apiKey?.toLowerCase().includes(q) ||
      s.whatsappPhone?.includes(q) ||
      slip.depositorName?.toLowerCase().includes(q) ||
      slip.referenceNumber?.toLowerCase().includes(q) ||
      slip.planName?.toLowerCase().includes(q) ||
      slip.status?.toLowerCase().includes(q);
  });

  const getPlanLabel = (type?: string) => {
    switch (type) {
      case 'setup_license': return 'Pro Setup License';
      case 'whitelabel_addon': return 'White-Label Add-on';
      case 'complete_pro_bundle': return 'Complete Pro Bundle';
      case 'maintenance_monthly': return 'Monthly Maintenance';
      case 'admin_direct_grant': return 'Admin Manual Grant';
      default: return type || 'Plan Upgrade';
    }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* ── Top Header Banner ── */}
      <div className="bg-gradient-to-r from-slate-950 via-[#0E1526] to-amber-950/40 border border-amber-500/30 rounded-2xl p-6 shadow-2xl relative overflow-hidden">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-400">
                <DollarSign size={20} />
              </div>
              <h2 className="text-xl font-black text-white tracking-tight font-mono">
                TENANT SUBSCRIPTIONS & PAYMENT LEDGER
              </h2>
              <span className="text-[10px] font-mono font-bold px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/40">
                PAYPAL REST & REVENUE
              </span>
            </div>
            <p className="text-xs text-slate-300 font-sans max-w-2xl">
              Monitor customer token consumption, trial quotas (100k initial), grant unlimited pro status, unlock white-label agency branding, and inspect verified PayPal checkout receipts.
            </p>
          </div>

          <button
            type="button"
            onClick={() => {
              fetchPayments();
              fetchSlips();
              fetchTokenCosts();
              if (onRefreshTenants) onRefreshTenants();
            }}
            disabled={loadingPayments || loadingCosts}
            className="px-4 py-2.5 bg-slate-900/90 hover:bg-slate-800 border border-white/10 hover:border-amber-500/50 rounded-xl text-xs font-mono font-bold text-slate-200 hover:text-white transition flex items-center gap-2 cursor-pointer shrink-0 shadow-lg"
          >
            <RefreshCw size={14} className={(loadingPayments || loadingCosts) ? 'animate-spin text-amber-400' : ''} />
            <span>Sync Live Data</span>
          </button>
        </div>
      </div>

      {/* ── KPI Metric Cards ── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total PayPal Revenue */}
        <div className="bg-[#0D1322] border border-amber-500/30 rounded-2xl p-4 shadow-xl">
          <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-2">
            <span className="font-bold">TOTAL VERIFIED REVENUE</span>
            <DollarSign size={16} className="text-amber-400" />
          </div>
          <div className="text-2xl font-black text-white font-mono">
            ${totalRevenueUsd.toFixed(2)}
          </div>
          <div className="text-[10px] text-slate-400 font-sans mt-1">
            Across {payments.length} verified checkout order(s)
          </div>
        </div>

        {/* Pro / Unlimited Accounts */}
        <div className="bg-[#0D1322] border border-emerald-500/30 rounded-2xl p-4 shadow-xl">
          <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-2">
            <span className="font-bold">PRO UNLIMITED</span>
            <Crown size={16} className="text-emerald-400" />
          </div>
          <div className="text-2xl font-black text-emerald-300 font-mono">
            {proCount} <span className="text-xs text-slate-400 font-sans font-normal">/ {tenants.length} tenants</span>
          </div>
          <div className="text-[10px] text-slate-400 font-sans mt-1">
            Unlimited tokens & priority runtime
          </div>
        </div>

        {/* Free Trial Accounts */}
        <div className="bg-[#0D1322] border border-cyan-500/30 rounded-2xl p-4 shadow-xl">
          <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-2">
            <span className="font-bold">100K FREE TRIAL</span>
            <Zap size={16} className="text-cyan-400" />
          </div>
          <div className="text-2xl font-black text-cyan-300 font-mono">
            {trialCount}
          </div>
          <div className="text-[10px] text-slate-400 font-sans mt-1">
            Active exploration accounts
          </div>
        </div>

        {/* White-Label Rebranded */}
        <div className="bg-[#0D1322] border border-purple-500/30 rounded-2xl p-4 shadow-xl">
          <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-2">
            <span className="font-bold">WHITE-LABEL ACTIVE</span>
            <ShieldCheck size={16} className="text-purple-400" />
          </div>
          <div className="text-2xl font-black text-purple-300 font-mono">
            {whiteLabelCount}
          </div>
          <div className="text-[10px] text-slate-400 font-sans mt-1">
            Custom agent name & custom avatar
          </div>
        </div>
      </div>

      {/* ── Sub-Tabs: Tenants vs PayPal Payments ── */}
      <div className="bg-slate-950/90 border border-white/10 rounded-2xl p-2 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setActiveTab('tenants')}
            className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition flex items-center gap-2 cursor-pointer ${
              activeTab === 'tenants'
                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Users size={14} />
            <span>Tenant Quotas ({tenants.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('token-costs')}
            className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition flex items-center gap-2 cursor-pointer ${
              activeTab === 'token-costs'
                ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Zap size={14} className="text-emerald-400" />
            <span>Token Cost Intelligence</span>
            {costData?.platformTotals?.totalCostLkr !== undefined && (
              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-mono font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                LKR {Number(costData.platformTotals.totalCostLkr).toLocaleString()}
              </span>
            )}
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('payments')}
            className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition flex items-center gap-2 cursor-pointer ${
              activeTab === 'payments'
                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <CreditCard size={14} />
            <span>PayPal Ledger ({payments.length})</span>
          </button>

          <button
            type="button"
            onClick={() => setActiveTab('slips')}
            className={`px-4 py-2 rounded-xl text-xs font-mono font-bold transition flex items-center gap-2 cursor-pointer ${
              activeTab === 'slips'
                ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-sm'
                : 'text-slate-400 hover:text-white hover:bg-white/5'
            }`}
          >
            <Receipt size={14} />
            <span>Bank Slips (LKR) ({slips.length})</span>
            {pendingSlipsCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-full text-[9px] font-black bg-rose-500 text-white animate-pulse">
                {pendingSlipsCount} PENDING
              </span>
            )}
          </button>
        </div>

        {/* Search Bar */}
        <div className="relative w-64 max-w-full">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            placeholder={activeTab === 'tenants' ? 'Search tenants, emails...' : activeTab === 'payments' ? 'Search payments, orders...' : 'Search slips, tenants, ref...'}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-900 border border-white/10 rounded-xl pl-9 pr-3 py-1.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-amber-400 font-mono"
          />
        </div>
      </div>

      {/* ── TAB 1: TENANTS SUBSCRIPTION & QUOTA TABLE ── */}
      {activeTab === 'tenants' && (
        <div className="space-y-4">
          {/* Filter Chips */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] font-mono text-slate-400 font-bold uppercase tracking-wider">Filter:</span>
            {[
              { id: 'all', label: `All (${tenants.length})` },
              { id: 'trial', label: `Basic Trial (${trialCount})` },
              { id: 'pro', label: `Pro Unlimited (${proCount})` },
              { id: 'whitelabel', label: `White-Label (${whiteLabelCount})` },
              { id: 'suspended', label: `Suspended (${suspendedCount})` },
            ].map(f => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilterType(f.id as any)}
                className={`px-2.5 py-1 rounded-lg text-xs font-mono transition cursor-pointer ${
                  filterType === f.id
                    ? 'bg-amber-500/20 text-amber-300 border border-amber-500/50 font-bold'
                    : 'bg-slate-900/60 text-slate-400 hover:text-white border border-slate-800'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>

          <div className="bg-slate-950/90 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-white/10 bg-slate-900/80 font-black text-slate-400 text-[10px] font-mono">
                    <th className="py-3.5 px-4 uppercase">Tenant Account</th>
                    <th className="py-3.5 px-4 uppercase">Plan Tier</th>
                    <th className="py-3.5 px-4 uppercase">Agent Tokens</th>
                    <th className="py-3.5 px-4 uppercase text-emerald-400">AI Cost (LKR & USD)</th>
                    <th className="py-3.5 px-4 uppercase">White-Label</th>
                    <th className="py-3.5 px-4 uppercase">Status</th>
                    <th className="py-3.5 px-4 uppercase">Subscription Due</th>
                    <th className="py-3.5 px-4 uppercase text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-sans">
                  {filteredTenants.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-slate-500 font-mono text-xs">
                        No tenants match current search or filter.
                      </td>
                    </tr>
                  ) : (
                    filteredTenants.map((t) => {
                      const isPro = t.planType === 'pro' || t.planType === 'enterprise';
                      const isWhiteLabel = t.isWhiteLabelEnabled === true;
                      const isSuspended = t.status === 'suspended';
                      const tokens = t.tokenBalance ?? 100000;
                      const dueDate = t.customFields?.subscriptionNextDueDate;

                      return (
                        <tr key={t.id} className="hover:bg-white/[0.02] transition">
                          {/* Tenant Info */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-white text-xs">{t.name}</div>
                            <div className="text-[10px] font-mono text-slate-400 flex items-center gap-1.5 mt-0.5">
                              <span>{t.adminEmail || 'No email attached'}</span>
                              <span className="text-slate-600">•</span>
                              <span className="text-cyan-400 font-mono">{t.apiKey ? `${t.apiKey.slice(0, 10)}...` : 'NO_KEY'}</span>
                            </div>
                          </td>

                          {/* Plan Tier */}
                          <td className="py-3.5 px-4 font-mono">
                            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border ${
                              isPro
                                ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                                : 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                            }`}>
                              {isPro && <Crown size={10} />}
                              <span>{t.planType ? t.planType.toUpperCase() : 'BASIC TRIAL'}</span>
                            </span>
                          </td>

                          {/* Token Balance */}
                          <td className="py-3.5 px-4 font-mono">
                            {isPro ? (
                              <div className="flex items-center gap-1.5 text-emerald-400 font-bold">
                                <Zap size={12} />
                                <span>Unlimited</span>
                              </div>
                            ) : (
                              <div>
                                <div className="text-white font-bold">{tokens.toLocaleString()}</div>
                                <div className="w-24 bg-slate-800 rounded-full h-1.5 mt-1 overflow-hidden">
                                  <div
                                    className="bg-cyan-400 h-1.5 rounded-full"
                                    style={{ width: `${Math.min(100, Math.max(5, (tokens / 100000) * 100))}%` }}
                                  />
                                </div>
                              </div>
                            )}
                          </td>

                          {/* AI Cost (LKR & USD) */}
                          <td className="py-3.5 px-4 font-mono">
                            {(() => {
                              const tCost = costData?.tenants?.find((c: any) => c.id === t.id);
                              const lkr = tCost?.tokens?.totals?.totalCostLkr || 0;
                              const usd = tCost?.tokens?.totals?.totalCostUsd || 0;
                              const savings = tCost?.tokens?.totals?.totalSavingsLkr || 0;
                              return (
                                <div>
                                  <div className="font-bold text-xs text-emerald-300">
                                    LKR {Number(lkr).toFixed(2)}
                                  </div>
                                  <div className="text-[9px] text-slate-400 font-mono mt-0.5 flex items-center gap-1 flex-wrap">
                                    <span>(${Number(usd).toFixed(4)})</span>
                                    {savings > 0 && (
                                      <span className="text-emerald-400 font-bold" title="Context Caching Savings">
                                        💾 -LKR {Number(savings).toFixed(2)}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              );
                            })()}
                          </td>

                          {/* White-Label Status */}
                          <td className="py-3.5 px-4 font-mono">
                            <button
                              type="button"
                              onClick={() => handleQuickToggleWhiteLabel(t)}
                              className={`px-2 py-0.5 rounded-lg text-[10px] font-bold transition flex items-center gap-1 cursor-pointer border ${
                                isWhiteLabel
                                  ? 'bg-purple-500/20 hover:bg-purple-500/30 text-purple-300 border-purple-500/40'
                                  : 'bg-slate-900 hover:bg-slate-800 text-slate-500 border-slate-800'
                              }`}
                              title="Click to toggle White-Label on/off"
                            >
                              {isWhiteLabel ? <Unlock size={10} /> : <Lock size={10} />}
                              <span>{isWhiteLabel ? 'UNLOCKED' : 'LOCKED'}</span>
                            </button>
                          </td>

                          {/* Account Status */}
                          <td className="py-3.5 px-4 font-mono">
                            <button
                              type="button"
                              onClick={() => handleQuickToggleSuspend(t)}
                              className={`px-2 py-0.5 rounded-full text-[10px] font-bold border transition flex items-center gap-1 cursor-pointer ${
                                isSuspended
                                  ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                                  : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                              }`}
                              title="Click to toggle suspend"
                            >
                              <span className={`w-1.5 h-1.5 rounded-full ${isSuspended ? 'bg-rose-400' : 'bg-emerald-400'}`} />
                              <span>{isSuspended ? 'SUSPENDED' : 'ACTIVE'}</span>
                            </button>
                          </td>

                          {/* Subscription Due */}
                          <td className="py-3.5 px-4 font-mono text-[11px] text-slate-300">
                            {dueDate ? (
                              <div className="flex items-center gap-1">
                                <Calendar size={11} className="text-slate-500" />
                                <span>{new Date(dueDate).toLocaleDateString()}</span>
                              </div>
                            ) : (
                              <span className="text-slate-500">Lifetime / N/A</span>
                            )}
                          </td>

                          {/* Action Button */}
                          <td className="py-3.5 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {(!isPro || !isWhiteLabel) && (
                                <button
                                  type="button"
                                  onClick={() => handleGrantFullBundle(t)}
                                  className="px-2.5 py-1.5 bg-gradient-to-r from-amber-500/20 to-orange-500/20 hover:from-amber-500/30 hover:to-orange-500/30 border border-amber-500/50 text-amber-300 rounded-xl text-xs font-mono font-bold transition flex items-center gap-1 cursor-pointer shadow-sm"
                                  title="1-Click Grant Full Bundle: Setup Paid + Unlimited Tokens + White-Label"
                                >
                                  <Crown size={12} className="text-amber-400" />
                                  <span>Unlock Full Bundle</span>
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => openEditModal(t)}
                                className="px-3 py-1.5 bg-amber-500/20 hover:bg-amber-500/30 border border-amber-500/40 text-amber-300 rounded-xl text-xs font-mono font-bold transition flex items-center gap-1.5 cursor-pointer"
                              >
                                <Sliders size={12} />
                                <span>Manage</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 2: PAYPAL & PAYMENT RECEIPTS TABLE ── */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          <div className="bg-slate-950/90 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-white/10 bg-slate-900/80 font-black text-slate-400 text-[10px] font-mono">
                    <th className="py-3.5 px-4 uppercase">Date & Time</th>
                    <th className="py-3.5 px-4 uppercase">Tenant Client</th>
                    <th className="py-3.5 px-4 uppercase">Purchased Plan</th>
                    <th className="py-3.5 px-4 uppercase">Amount</th>
                    <th className="py-3.5 px-4 uppercase">Gateway / Method</th>
                    <th className="py-3.5 px-4 uppercase">PayPal Order ID</th>
                    <th className="py-3.5 px-4 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-sans">
                  {filteredPayments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-12 text-slate-500 font-mono text-xs">
                        No transactions recorded yet. Payments completed via PayPal will automatically log here in real-time.
                      </td>
                    </tr>
                  ) : (
                    filteredPayments.map((p, idx) => (
                      <tr key={idx} className="hover:bg-white/[0.02] transition font-mono">
                        {/* Date */}
                        <td className="py-3.5 px-4 text-slate-300 text-[11px]">
                          {p.date ? new Date(p.date).toLocaleString() : 'Recent'}
                        </td>

                        {/* Tenant */}
                        <td className="py-3.5 px-4">
                          <div className="font-bold text-white font-sans text-xs">{p.tenantName || `Tenant #${p.tenantId}`}</div>
                          <div className="text-[10px] text-slate-500 font-mono">{p.apiKey ? `${p.apiKey.slice(0, 10)}...` : ''}</div>
                        </td>

                        {/* Purchased Plan */}
                        <td className="py-3.5 px-4">
                          <span className="text-amber-300 font-bold flex items-center gap-1.5">
                            <Crown size={12} className="text-amber-400" />
                            <span>{getPlanLabel(p.planType)}</span>
                          </span>
                        </td>

                        {/* Amount */}
                        <td className="py-3.5 px-4">
                          <div className="font-black text-emerald-400 text-sm">
                            ${Number(p.amountUsd || p.amount || 0).toFixed(2)}
                          </div>
                          {p.amountLkr && (
                            <div className="text-[10px] text-slate-400 font-sans">
                              Rs. {Number(p.amountLkr).toLocaleString()} LKR
                            </div>
                          )}
                        </td>

                        {/* Gateway */}
                        <td className="py-3.5 px-4">
                          <span className="px-2 py-0.5 rounded text-[10px] bg-blue-500/20 text-blue-300 border border-blue-500/30">
                            {p.method || 'PayPal REST'}
                          </span>
                        </td>

                        {/* Order ID */}
                        <td className="py-3.5 px-4 text-slate-400 font-mono text-[11px] truncate max-w-[140px]" title={p.orderId}>
                          {p.orderId || 'DIRECT_GRANT'}
                        </td>

                        {/* Status */}
                        <td className="py-3.5 px-4">
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-1 w-fit">
                            <CheckCircle2 size={10} />
                            <span>{p.status ? p.status.toUpperCase() : 'COMPLETED'}</span>
                          </span>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 3: BANK TRANSFER SLIPS LEDGER (SRI LANKA) ── */}
      {activeTab === 'slips' && (
        <div className="space-y-4">
          {/* Information & Action Header */}
          <div className="bg-[#0B101B] border border-amber-500/30 rounded-2xl p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-amber-500/10 border border-amber-500/30 rounded-xl text-amber-400">
                <Receipt size={20} />
              </div>
              <div>
                <h3 className="text-sm font-black text-white font-mono flex items-center gap-2">
                  <span>DIRECT BANK TRANSFER VERIFICATION</span>
                  {pendingSlipsCount > 0 && (
                    <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-rose-500/20 text-rose-300 border border-rose-500/40">
                      {pendingSlipsCount} Awaiting Review
                    </span>
                  )}
                </h3>
                <p className="text-xs text-slate-400 font-sans mt-0.5">
                  Verify Commercial Bank of Ceylon deposit slips, match bank statement reference numbers, and 1-click activate tenant plans.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={fetchSlips}
              disabled={loadingSlips}
              className="px-3.5 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-slate-300 border border-white/10 text-xs font-mono flex items-center gap-2 cursor-pointer transition disabled:opacity-50"
            >
              <RefreshCw size={13} className={loadingSlips ? 'animate-spin' : ''} />
              <span>Refresh Slips</span>
            </button>
          </div>

          {/* Slips Table */}
          <div className="bg-slate-950/90 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-white/10 bg-slate-900/80 font-black text-slate-400 text-[10px] font-mono">
                    <th className="py-3.5 px-4 uppercase">Tenant Account</th>
                    <th className="py-3.5 px-4 uppercase">Requested Package</th>
                    <th className="py-3.5 px-4 uppercase">Amount (LKR)</th>
                    <th className="py-3.5 px-4 uppercase">Depositor & Reference</th>
                    <th className="py-3.5 px-4 uppercase">Deposit Slip</th>
                    <th className="py-3.5 px-4 uppercase">Submitted Time</th>
                    <th className="py-3.5 px-4 uppercase">Status</th>
                    <th className="py-3.5 px-4 uppercase text-right">Verification Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-sans">
                  {loadingSlips ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-slate-500 font-mono text-xs">
                        <RefreshCw size={18} className="animate-spin inline-block mr-2" />
                        Loading bank transfer slips...
                      </td>
                    </tr>
                  ) : filteredSlips.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="text-center py-12 text-slate-500 font-mono text-xs">
                        {searchQuery ? 'No bank slips match your search criteria.' : 'No bank deposit slips submitted yet. Once tenants upload slips, they appear here.'}
                      </td>
                    </tr>
                  ) : (
                    filteredSlips.map((s, idx) => {
                      const slip = s.slip || {};
                      const isPending = slip.status === 'pending_verification' || slip.status === 'pending';
                      const isApproved = slip.status === 'approved';
                      const isRejected = slip.status === 'rejected';
                      const isLoadingAction = slipActionLoading === String(s.tenantId);

                      return (
                        <tr key={idx} className="hover:bg-white/[0.02] transition font-sans">
                          {/* Tenant Account */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-white text-xs">{s.tenantName || `Tenant #${s.tenantId}`}</div>
                            <div className="text-[10px] text-slate-400 font-mono flex items-center gap-1 mt-0.5">
                              {s.whatsappPhone && (
                                <span className="text-emerald-400 flex items-center gap-1">
                                  <Phone size={10} />
                                  <span>{s.whatsappPhone}</span>
                                </span>
                              )}
                              <span>•</span>
                              <span>API: {s.apiKey ? `${s.apiKey.slice(0, 8)}...` : 'N/A'}</span>
                            </div>
                          </td>

                          {/* Requested Package */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-amber-300 flex items-center gap-1.5 font-mono">
                              <Crown size={12} className="text-amber-400" />
                              <span>{slip.planName || getPlanLabel(slip.planType)}</span>
                            </div>
                            <div className="text-[10px] text-slate-400 uppercase tracking-wider font-mono mt-0.5">
                              {slip.planType}
                            </div>
                          </td>

                          {/* Amount */}
                          <td className="py-3.5 px-4 font-mono">
                            <div className="font-black text-emerald-400 text-sm">
                              Rs. {Number(slip.amount || 0).toLocaleString()}
                            </div>
                            <div className="text-[10px] text-slate-500 uppercase">
                              {slip.currency || 'LKR'}
                            </div>
                          </td>

                          {/* Depositor & Reference */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-white text-xs">
                              {slip.depositorName || 'Unknown Depositor'}
                            </div>
                            <div className="mt-1 flex items-center gap-1 font-mono">
                              <span className="text-[9px] text-slate-500 uppercase">REF:</span>
                              <span className="px-1.5 py-0.5 bg-slate-900 border border-white/10 rounded text-cyan-300 text-[10px] font-bold">
                                {slip.referenceNumber || 'NO REF'}
                              </span>
                            </div>
                            {slip.notes && (
                              <div className="text-[10px] text-slate-400 italic mt-1 max-w-[180px] truncate" title={slip.notes}>
                                "{slip.notes}"
                              </div>
                            )}
                          </td>

                          {/* Deposit Slip Preview */}
                          <td className="py-3.5 px-4">
                            {slip.slipUrl ? (
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => setSelectedSlipImage(slip.slipUrl)}
                                  className="relative group block w-14 h-14 rounded-xl overflow-hidden border border-white/10 hover:border-amber-400/80 transition cursor-pointer shadow-md bg-slate-900"
                                >
                                  <img
                                    src={slip.slipUrl}
                                    alt="Slip Thumbnail"
                                    className="w-full h-full object-cover group-hover:scale-110 transition duration-200"
                                  />
                                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                                    <Eye size={16} className="text-white" />
                                  </div>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setSelectedSlipImage(slip.slipUrl)}
                                  className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 hover:text-white border border-white/10 transition cursor-pointer"
                                  title="View Full Slip"
                                >
                                  <Eye size={13} />
                                </button>
                              </div>
                            ) : (
                              <span className="text-slate-500 italic text-[11px]">No image attached</span>
                            )}
                          </td>

                          {/* Submitted Time */}
                          <td className="py-3.5 px-4 text-slate-400 font-mono text-[11px]">
                            {slip.submittedAt ? (
                              <div>
                                <div>{new Date(slip.submittedAt).toLocaleDateString()}</div>
                                <div className="text-[10px] text-slate-500">{new Date(slip.submittedAt).toLocaleTimeString()}</div>
                              </div>
                            ) : (
                              'Recent'
                            )}
                          </td>

                          {/* Status */}
                          <td className="py-3.5 px-4">
                            {isPending && (
                              <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-amber-500/20 text-amber-300 border border-amber-500/40 flex items-center gap-1.5 w-fit font-mono animate-pulse">
                                <Clock size={11} />
                                <span>PENDING</span>
                              </span>
                            )}
                            {isApproved && (
                              <div>
                                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 flex items-center gap-1.5 w-fit font-mono">
                                  <CheckCircle2 size={11} />
                                  <span>APPROVED</span>
                                </span>
                                {slip.approvedAt && (
                                  <div className="text-[9px] text-slate-500 font-mono mt-0.5">
                                    {new Date(slip.approvedAt).toLocaleDateString()}
                                  </div>
                                )}
                              </div>
                            )}
                            {isRejected && (
                              <div>
                                <span className="px-2.5 py-1 rounded-full text-[10px] font-bold bg-rose-500/20 text-rose-300 border border-rose-500/40 flex items-center gap-1.5 w-fit font-mono">
                                  <AlertCircle size={11} />
                                  <span>REJECTED</span>
                                </span>
                                {slip.rejectionReason && (
                                  <div className="text-[10px] text-rose-400/80 italic mt-1 max-w-[160px] truncate" title={slip.rejectionReason}>
                                    {slip.rejectionReason}
                                  </div>
                                )}
                              </div>
                            )}
                          </td>

                          {/* Verification Action */}
                          <td className="py-3.5 px-4 text-right">
                            <div className="flex items-center justify-end gap-2">
                              {isPending ? (
                                <>
                                  <button
                                    type="button"
                                    onClick={() => handleApproveSlip(s.tenantId, slip.planName || 'Plan', s.tenantName)}
                                    disabled={isLoadingAction}
                                    className="px-3 py-1.5 bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-400 hover:to-emerald-500 text-slate-950 font-black rounded-xl text-xs font-mono transition shadow-md flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                                  >
                                    {isLoadingAction ? (
                                      <RefreshCw size={12} className="animate-spin" />
                                    ) : (
                                      <Check size={12} />
                                    )}
                                    <span>Approve & Activate</span>
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() => {
                                      setRejectModalTenant(s);
                                      setRejectReason('');
                                    }}
                                    disabled={isLoadingAction}
                                    className="px-2.5 py-1.5 bg-rose-500/10 hover:bg-rose-500/20 text-rose-300 border border-rose-500/30 rounded-xl text-xs font-mono transition cursor-pointer disabled:opacity-50"
                                  >
                                    <X size={12} />
                                    <span>Reject</span>
                                  </button>
                                </>
                              ) : isApproved ? (
                                <span className="text-[11px] font-mono text-emerald-400 font-bold flex items-center gap-1">
                                  <CheckCircle2 size={12} />
                                  <span>Active on Platform</span>
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => handleApproveSlip(s.tenantId, slip.planName || 'Plan', s.tenantName)}
                                  disabled={isLoadingAction}
                                  className="px-2.5 py-1 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/30 rounded-xl text-[11px] font-mono transition cursor-pointer disabled:opacity-50"
                                >
                                  Re-Approve
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── TAB 4: AI TOKEN COST INTELLIGENCE (LKR & USD) ── */}
      {activeTab === 'token-costs' && (
        <div className="space-y-6 animate-in fade-in">
          {/* Top Control Bar with Exchange Rate */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-slate-950/90 border border-emerald-500/30 rounded-2xl p-4 shadow-xl">
            <div>
              <div className="flex items-center gap-2">
                <div className="p-2 rounded-xl bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                  <Calculator size={18} />
                </div>
                <div>
                  <h3 className="text-sm font-black text-white font-mono flex items-center gap-2">
                    <span>LUNA AI TOKEN FINANCIAL INTELLIGENCE</span>
                    <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                      100% AUDIT ACCURATE
                    </span>
                  </h3>
                  <p className="text-[11px] text-slate-400 font-sans mt-0.5">
                    User-by-user cost accounting for Luna Live Core & Auxiliary Neural Engine with 90% context cache savings.
                  </p>
                </div>
              </div>
            </div>

            {/* Currency Rate Controller */}
            <div className="flex items-center gap-3 self-end md:self-center">
              <div className="flex items-center gap-2 bg-slate-900 border border-white/10 rounded-xl px-3 py-1.5 font-mono text-xs">
                <span className="text-slate-400">1 USD =</span>
                <input
                  type="number"
                  value={costRateInput}
                  onChange={(e) => setCostRateInput(parseFloat(e.target.value) || 305)}
                  className="w-16 bg-slate-950 border border-emerald-500/40 rounded px-1.5 py-0.5 text-center text-emerald-300 font-bold focus:outline-none"
                />
                <span className="text-emerald-400 font-bold">LKR</span>
                <button
                  type="button"
                  onClick={() => fetchTokenCosts(costRateInput)}
                  className="px-2 py-1 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 rounded font-bold transition text-[10px] cursor-pointer"
                  title="Recalculate with new exchange rate"
                >
                  Recalculate
                </button>
              </div>

              <button
                type="button"
                onClick={() => fetchTokenCosts()}
                disabled={loadingCosts}
                className="p-2 bg-slate-900 hover:bg-slate-800 border border-white/10 text-slate-300 hover:text-white rounded-xl transition cursor-pointer"
                title="Refresh token costs"
              >
                <RefreshCw size={14} className={loadingCosts ? 'animate-spin text-emerald-400' : ''} />
              </button>
            </div>
          </div>

          {/* 6 Executive KPI Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3">
            {/* 1. Net Billable Master Revenue */}
            <div className="bg-gradient-to-br from-[#1c1204] to-[#0e0901] border border-amber-500/40 rounded-2xl p-3.5 shadow-xl">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-1">
                <span className="font-bold text-amber-300 text-[10px] uppercase">Billable Master AI</span>
                <Zap size={14} className="text-amber-400" />
              </div>
              <div className="text-lg font-black text-amber-200 font-mono">
                LKR {Number(costData?.platformTotals?.billableCostLkr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[9.5px] font-mono text-slate-400 mt-1 flex items-center justify-between">
                <span>${Number(costData?.platformTotals?.billableCostUsd || 0).toFixed(4)} USD</span>
                <span className="text-amber-400 font-bold">{Number(costData?.platformTotals?.paidMasterTokens || 0).toLocaleString()} tok</span>
              </div>
            </div>

            {/* 2. Client Free Tokens */}
            <div className="bg-gradient-to-br from-[#051c14] to-[#020b08] border border-emerald-500/40 rounded-2xl p-3.5 shadow-xl">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-1">
                <span className="font-bold text-emerald-300 text-[10px] uppercase">Free Dedicated Tokens</span>
                <Sparkles size={14} className="text-emerald-400" />
              </div>
              <div className="text-lg font-black text-emerald-200 font-mono">
                {Number(costData?.platformTotals?.freeTokens || 0).toLocaleString()}
              </div>
              <div className="text-[9.5px] font-mono text-emerald-400 mt-1 flex items-center justify-between">
                <span>LKR 0.00 ($0.00)</span>
                <span className="text-[8.5px] text-slate-400">Saved Rs. {Number(costData?.platformTotals?.clientSavedLkr || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
              </div>
            </div>

            {/* 3. Main Core Brain */}
            <div className="bg-gradient-to-br from-[#081f2c] to-[#04111a] border border-sky-500/30 rounded-2xl p-3.5 shadow-xl">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-1">
                <span className="font-bold text-sky-300 text-[10px] uppercase">Core Brain (Text/WA)</span>
                <Bot size={14} className="text-sky-400" />
              </div>
              <div className="text-lg font-black text-sky-200 font-mono">
                LKR {Number(costData?.platformTotals?.mainBrain?.costLkr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[9.5px] font-mono text-slate-400 mt-1">
                ${Number(costData?.platformTotals?.mainBrain?.costUsd || 0).toFixed(4)} USD
              </div>
            </div>

            {/* 4. Live Voice Calls */}
            <div className="bg-gradient-to-br from-[#1b0d2e] to-[#0f071c] border border-purple-500/30 rounded-2xl p-3.5 shadow-xl">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-1">
                <span className="font-bold text-purple-300 text-[10px] uppercase">Voice Calls (SIP)</span>
                <PhoneCall size={14} className="text-purple-400" />
              </div>
              <div className="text-lg font-black text-purple-200 font-mono">
                LKR {Number(costData?.platformTotals?.calls?.costLkr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[9.5px] font-mono text-slate-400 mt-1">
                ${Number(costData?.platformTotals?.calls?.costUsd || 0).toFixed(4)} USD
              </div>
            </div>

            {/* 5. Training Vault */}
            <div className="bg-gradient-to-br from-[#072018] to-[#04130e] border border-emerald-500/30 rounded-2xl p-3.5 shadow-xl">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-1">
                <span className="font-bold text-emerald-300 text-[10px] uppercase">Training Vault</span>
                <BookOpen size={14} className="text-emerald-400" />
              </div>
              <div className="text-lg font-black text-emerald-200 font-mono">
                LKR {Number(costData?.platformTotals?.trainingVault?.costLkr || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-[9.5px] font-mono text-slate-400 mt-1">
                ${Number(costData?.platformTotals?.trainingVault?.costUsd || 0).toFixed(4)} USD
              </div>
            </div>

            {/* 6. Total Platform Consumption */}
            <div className="bg-gradient-to-br from-[#0c1829] to-[#07101e] border border-cyan-500/30 rounded-2xl p-3.5 shadow-xl">
              <div className="flex items-center justify-between text-slate-400 text-xs font-mono mb-1">
                <span className="font-bold text-cyan-300 text-[10px] uppercase">Total Consumption</span>
                <DollarSign size={14} className="text-cyan-400" />
              </div>
              <div className="text-lg font-black text-cyan-200 font-mono">
                {Number(costData?.platformTotals?.totalTokens || 0).toLocaleString()}
              </div>
              <div className="text-[9.5px] font-mono text-slate-400 mt-1 flex items-center justify-between">
                <span>Gross: Rs. {Number(costData?.platformTotals?.totalCostLkr || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                <span className="text-emerald-400 font-bold">🎉 Rs. {Number(costData?.platformTotals?.totalSavingsLkr || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} saved</span>
              </div>
            </div>
          </div>

          {/* Search & Sort Filters */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 bg-slate-950/80 border border-white/10 rounded-2xl p-3">
            <div className="relative w-full sm:w-80">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search tenant name, email, API key..."
                value={costSearchQuery}
                onChange={(e) => setCostSearchQuery(e.target.value)}
                className="w-full bg-slate-900 border border-white/10 rounded-xl pl-9 pr-3 py-1.5 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-emerald-400 font-mono"
              />
            </div>

            <div className="flex items-center gap-2 self-end sm:self-center font-mono text-xs text-slate-400">
              <span>Sort by:</span>
              <button
                type="button"
                onClick={() => setCostSortBy('cost')}
                className={`px-2.5 py-1 rounded-lg text-xs font-mono transition cursor-pointer ${
                  costSortBy === 'cost' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40 font-bold' : 'bg-slate-900 text-slate-400'
                }`}
              >
                Billable Master
              </button>
              <button
                type="button"
                onClick={() => setCostSortBy('tokens')}
                className={`px-2.5 py-1 rounded-lg text-xs font-mono transition cursor-pointer ${
                  costSortBy === 'tokens' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold' : 'bg-slate-900 text-slate-400'
                }`}
              >
                Tokens
              </button>
              <button
                type="button"
                onClick={() => setCostSortBy('name')}
                className={`px-2.5 py-1 rounded-lg text-xs font-mono transition cursor-pointer ${
                  costSortBy === 'name' ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 font-bold' : 'bg-slate-900 text-slate-400'
                }`}
              >
                Name
              </button>
            </div>
          </div>

          {/* Complete User-by-User Financial Ledger Table */}
          <div className="bg-slate-950/90 border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left">
                <thead>
                  <tr className="border-b border-white/10 bg-slate-900/90 font-black text-slate-400 text-[10px] font-mono uppercase">
                    <th className="py-3.5 px-4">Tenant Client</th>
                    <th className="py-3.5 px-4 text-emerald-300">Your Free Tokens (Dedicated)</th>
                    <th className="py-3.5 px-4 text-amber-300">Paid Master AI (Billable)</th>
                    <th className="py-3.5 px-4 text-sky-300">Core Brain (Text)</th>
                    <th className="py-3.5 px-4 text-purple-300">Voice (SIP)</th>
                    <th className="py-3.5 px-4 text-emerald-400 text-right">Net Billable Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-white/5 font-sans">
                  {(() => {
                    let rows = (costData?.tenants || []) as any[];
                    if (costSearchQuery.trim()) {
                      const q = costSearchQuery.toLowerCase();
                      rows = rows.filter(
                        (r) =>
                          r.name?.toLowerCase().includes(q) ||
                          r.adminEmail?.toLowerCase().includes(q) ||
                          r.apiKey?.toLowerCase().includes(q)
                      );
                    }
                    if (costSortBy === 'cost') {
                      rows.sort((a, b) => (b.billableCostLkr || 0) - (a.billableCostLkr || 0) || (b.tokens?.totals?.totalTokens || 0) - (a.tokens?.totals?.totalTokens || 0));
                    } else if (costSortBy === 'tokens') {
                      rows.sort((a, b) => (b.tokens?.totals?.totalTokens || 0) - (a.tokens?.totals?.totalTokens || 0));
                    } else if (costSortBy === 'name') {
                      rows.sort((a, b) => a.name?.localeCompare(b.name));
                    }

                    if (rows.length === 0) {
                      return (
                        <tr>
                          <td colSpan={6} className="text-center py-12 text-slate-500 font-mono text-xs">
                            No tenant cost records found matching filter.
                          </td>
                        </tr>
                      );
                    }

                    return rows.map((t: any) => {
                      const mb = t.tokens?.mainBrain || {};
                      const calls = t.tokens?.calls || {};
                      const vault = t.tokens?.trainingVault || {};
                      const tot = t.tokens?.totals || {};
                      const freeTok = t.freeTokens || {};
                      const paidTok = t.paidMasterTokens || {};

                      return (
                        <tr key={t.id} className="hover:bg-white/[0.02] transition font-mono">
                          {/* Tenant Info */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-white text-xs">{t.name}</div>
                            <div className="text-[10px] text-slate-400 flex items-center gap-1.5 mt-0.5">
                              <span>{t.adminEmail || 'No email attached'}</span>
                              <span className="text-slate-600">•</span>
                              <span className="text-cyan-400">{t.apiKey ? `${t.apiKey.slice(0, 10)}...` : 'NO_KEY'}</span>
                            </div>
                            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                              <span className={`px-1.5 py-0.2 rounded text-[9px] font-bold border ${
                                t.planType === 'pro' || t.planType === 'enterprise'
                                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                                  : 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                              }`}>
                                {t.planType ? t.planType.toUpperCase() : 'TRIAL'}
                              </span>
                              <span className={`px-1.5 py-0.2 rounded text-[8.5px] font-bold border ${
                                t.hasDedicatedKeys
                                  ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                                  : 'bg-slate-800 text-slate-400 border-slate-700'
                              }`}>
                                {t.hasDedicatedKeys ? `🔑 ${t.poolSize || 1} Keys In Pool` : '⚡ Master AI Only'}
                              </span>
                            </div>
                          </td>

                          {/* Free Dedicated Tokens */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-emerald-300 text-xs">
                              {Number(freeTok.total || 0).toLocaleString()} Tokens
                            </div>
                            <div className="text-[9px] text-emerald-400 mt-0.5 font-bold">
                              Cost: LKR 0.00 ($0.00)
                            </div>
                            <div className="text-[8px] text-slate-400 mt-1">
                              🎉 Client Saved: LKR {Number(freeTok.savingsLkr || 0).toFixed(2)}
                            </div>
                          </td>

                          {/* Paid Master AI Tokens (Billable) */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-amber-300 text-xs">
                              {Number(paidTok.total || 0).toLocaleString()} Tokens
                            </div>
                            <div className={`text-[9px] mt-0.5 font-bold ${
                              Number(paidTok.costLkr || 0) > 0 ? 'text-amber-400' : 'text-slate-400'
                            }`}>
                              Billable: LKR {Number(paidTok.costLkr || 0).toFixed(2)} (${Number(paidTok.costUsd || 0).toFixed(4)})
                            </div>
                            <div className="text-[8px] mt-1">
                              <span className={`px-1 py-0.2 rounded text-[8px] font-bold border ${
                                Number(paidTok.total || 0) > 0
                                  ? 'bg-rose-500/20 text-rose-300 border-rose-500/40'
                                  : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                              }`}>
                                {Number(paidTok.total || 0) > 0 ? 'FAILOVER OVERAGES' : 'ZERO OVERAGE'}
                              </span>
                            </div>
                          </td>

                          {/* Main Core Brain */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-sky-300 text-xs">
                              {Number(mb.totalTokens || 0).toLocaleString()} tok
                            </div>
                            <div className="text-[9px] text-slate-400 mt-0.5">
                              LKR {Number(mb.costLkr || 0).toFixed(2)} (${Number(mb.costUsd || 0).toFixed(4)})
                            </div>
                            <div className="text-[8px] text-slate-500 mt-1 flex items-center gap-1 flex-wrap">
                              <span>Fresh: {Number(mb.freshInput || 0).toLocaleString()}</span>
                              <span>•</span>
                              <span className="text-emerald-400 font-bold">💾 {Number(mb.cachedInput || 0).toLocaleString()}</span>
                            </div>
                          </td>

                          {/* Live Voice Calls */}
                          <td className="py-3.5 px-4">
                            <div className="font-bold text-purple-300 text-xs">
                              {Number(calls.totalTokens || 0).toLocaleString()} tok
                            </div>
                            <div className="text-[9px] text-slate-400 mt-0.5">
                              LKR {Number(calls.costLkr || 0).toFixed(2)} (${Number(calls.costUsd || 0).toFixed(4)})
                            </div>
                            <div className="text-[8px] text-slate-500 mt-1">
                              {Math.floor((calls.durationSeconds || 0) / 60)}m {(calls.durationSeconds || 0) % 60}s ({calls.callCount || 0} calls)
                            </div>
                          </td>

                          {/* Net Billable Amount */}
                          <td className="py-3.5 px-4 text-right">
                            {Number(t.billableCostLkr || 0) > 0 ? (
                              <div>
                                <div className="font-black text-sm text-amber-400">
                                  LKR {Number(t.billableCostLkr || 0).toFixed(2)}
                                </div>
                                <div className="text-[10px] text-slate-300 mt-0.5">
                                  ${Number(t.billableCostUsd || 0).toFixed(4)} USD
                                </div>
                                <div className="text-[8px] text-rose-400/90 mt-1">
                                  Payable from Master AI
                                </div>
                              </div>
                            ) : (
                              <div>
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-500/20 text-emerald-300 border border-emerald-500/40">
                                  <Sparkles size={10} />
                                  <span>LKR 0.00 (Free)</span>
                                </span>
                                <div className="text-[8px] text-emerald-400/80 mt-1">
                                  100% Dedicated Pool
                                </div>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    });
                  })()}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: EDIT TENANT SUBSCRIPTION ── */}
      {selectedTenant && (
        <div className="fixed inset-0 z-[99999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0B101B] border border-amber-500/40 rounded-2xl w-full max-w-lg overflow-hidden shadow-[0_0_60px_rgba(245,158,11,0.25)] animate-in fade-in zoom-in-95">
            {/* Header */}
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-amber-950/40 to-slate-900">
              <div className="flex items-center gap-2.5">
                <div className="p-2 rounded-xl bg-amber-500/20 text-amber-300 border border-amber-500/30">
                  <Sliders size={16} />
                </div>
                <div>
                  <h3 className="text-sm font-black text-white font-mono">
                    MANAGE TENANT SUBSCRIPTION
                  </h3>
                  <p className="text-xs text-slate-400 font-sans">
                    {selectedTenant.name} ({selectedTenant.adminEmail || selectedTenant.apiKey?.slice(0, 10)})
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedTenant(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4 text-xs">
              {modalFeedback && (
                <div className={`p-3 rounded-xl border flex items-center gap-2 ${
                  modalFeedback.type === 'success'
                    ? 'bg-emerald-950/40 border-emerald-500/40 text-emerald-200'
                    : 'bg-rose-950/40 border-rose-500/40 text-rose-200'
                }`}>
                  {modalFeedback.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
                  <span>{modalFeedback.message}</span>
                </div>
              )}

              {/* 1-Click Quick Full Bundle Action */}
              <button
                type="button"
                onClick={() => {
                  setEditPlan('pro');
                  setEditWhiteLabel(true);
                  setEditDaysToAdd(30);
                  setEditStatus('active');
                }}
                className="w-full p-3 rounded-xl bg-gradient-to-r from-amber-500/20 via-purple-500/20 to-cyan-500/20 border border-amber-500/50 hover:border-amber-400 text-amber-200 font-mono font-bold flex items-center justify-between transition cursor-pointer shadow-md hover:scale-[1.01]"
              >
                <span className="flex items-center gap-2">
                  <Crown size={15} className="text-amber-400" />
                  <span>1-Click Activate Full Pro Bundle</span>
                </span>
                <span className="text-[10px] bg-amber-500 text-slate-950 font-black px-2 py-0.5 rounded-full uppercase">
                  Setup Paid + Unlimited + White-Label
                </span>
              </button>

              {/* Plan Tier Selector */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-mono font-bold text-slate-400 uppercase">
                  Plan Tier
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: 'basic_trial', label: 'Basic Trial', note: '100k Tokens Cap' },
                    { id: 'pro', label: 'Pro Unlimited', note: 'Unlimited Tokens' },
                    { id: 'enterprise', label: 'Enterprise', note: 'Priority Multi-Agent' },
                  ].map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => setEditPlan(p.id as any)}
                      className={`p-2.5 rounded-xl border text-left transition cursor-pointer ${
                        editPlan === p.id
                          ? 'bg-amber-500/20 border-amber-500/60 text-white'
                          : 'bg-slate-900 border-white/5 text-slate-400 hover:border-white/20'
                      }`}
                    >
                      <div className="font-bold text-xs">{p.label}</div>
                      <div className="text-[10px] text-slate-400 font-sans mt-0.5">{p.note}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* White-Label Rebranding Toggle */}
              <div className="bg-slate-900/80 border border-white/5 rounded-xl p-3.5 flex items-center justify-between">
                <div>
                  <div className="font-bold text-white flex items-center gap-1.5">
                    <Crown size={13} className="text-purple-400" />
                    <span>White-Label Branding Permission</span>
                  </div>
                  <p className="text-[11px] text-slate-400 mt-0.5 font-sans">
                    Allows tenant to rename agent and upload custom avatars without "Luna AI" locks.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setEditWhiteLabel(!editWhiteLabel)}
                  className={`px-3 py-1.5 rounded-xl text-xs font-mono font-bold border transition cursor-pointer ${
                    editWhiteLabel
                      ? 'bg-purple-500/20 text-purple-300 border-purple-500/50'
                      : 'bg-slate-800 text-slate-500 border-slate-700'
                  }`}
                >
                  {editWhiteLabel ? 'ENABLED' : 'LOCKED'}
                </button>
              </div>

              {/* Add Tokens */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-mono font-bold text-slate-400 uppercase flex items-center justify-between">
                  <span>Grant Additional Agent Tokens</span>
                  <span className="text-cyan-400">Current: {(selectedTenant.tokenBalance ?? 100000).toLocaleString()}</span>
                </label>
                <div className="grid grid-cols-4 gap-2 font-mono text-xs">
                  {[
                    { add: 0, label: 'No Change' },
                    { add: 50000, label: '+50k' },
                    { add: 100000, label: '+100k' },
                    { add: 500000, label: '+500k' },
                  ].map(b => (
                    <button
                      key={b.add}
                      type="button"
                      onClick={() => setEditTokensToAdd(b.add)}
                      className={`py-2 rounded-xl border text-center transition cursor-pointer font-bold ${
                        editTokensToAdd === b.add
                          ? 'bg-cyan-500/20 border-cyan-500/60 text-cyan-300'
                          : 'bg-slate-900 border-white/5 text-slate-400 hover:border-white/20'
                      }`}
                    >
                      {b.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Additional Subscription Days */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-mono font-bold text-slate-400 uppercase">
                  Extend Validity / Next Due Date
                </label>
                <div className="grid grid-cols-4 gap-2 font-mono text-xs">
                  {[
                    { days: 0, label: 'Keep Same' },
                    { days: 30, label: '+30 Days' },
                    { days: 90, label: '+90 Days' },
                    { days: 365, label: '+1 Year' },
                  ].map(d => (
                    <button
                      key={d.days}
                      type="button"
                      onClick={() => setEditDaysToAdd(d.days)}
                      className={`py-2 rounded-xl border text-center transition cursor-pointer font-bold ${
                        editDaysToAdd === d.days
                          ? 'bg-amber-500/20 border-amber-500/60 text-amber-300'
                          : 'bg-slate-900 border-white/5 text-slate-400 hover:border-white/20'
                      }`}
                    >
                      {d.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Account Status Toggle */}
              <div className="space-y-1.5">
                <label className="text-[11px] font-mono font-bold text-slate-400 uppercase">
                  Account Status
                </label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setEditStatus('active')}
                    className={`py-2 rounded-xl border text-center font-bold font-mono transition cursor-pointer ${
                      editStatus === 'active'
                        ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                        : 'bg-slate-900 border-white/5 text-slate-400'
                    }`}
                  >
                    Active Account
                  </button>
                  <button
                    type="button"
                    onClick={() => setEditStatus('suspended')}
                    className={`py-2 rounded-xl border text-center font-bold font-mono transition cursor-pointer ${
                      editStatus === 'suspended'
                        ? 'bg-rose-500/20 border-rose-500/50 text-rose-300'
                        : 'bg-slate-900 border-white/5 text-slate-400'
                    }`}
                  >
                    Suspended (Locked)
                  </button>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-white/10 bg-slate-950/60 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setSelectedTenant(null)}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-xl text-xs font-mono transition cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSaveSubscription}
                disabled={subActionLoading}
                className="px-5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-black rounded-xl text-xs font-mono transition shadow-lg flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {subActionLoading && <RefreshCw size={13} className="animate-spin" />}
                <span>Save Changes</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: HIGH-RES SLIP LIGHTBOX ── */}
      {selectedSlipImage && (
        <div className="fixed inset-0 z-[999999] bg-black/90 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0B101B] border border-amber-500/40 rounded-2xl w-full max-w-3xl overflow-hidden shadow-[0_0_80px_rgba(245,158,11,0.3)] animate-in fade-in zoom-in-95 flex flex-col max-h-[90vh]">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-gradient-to-r from-amber-950/40 to-slate-900">
              <div className="flex items-center gap-2">
                <Receipt size={16} className="text-amber-400" />
                <h3 className="text-sm font-black text-white font-mono">
                  BANK DEPOSIT SLIP INSPECTOR
                </h3>
              </div>
              <div className="flex items-center gap-2">
                <a
                  href={selectedSlipImage}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-1 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-xs font-mono flex items-center gap-1.5 transition"
                >
                  <ExternalLink size={12} />
                  <span>Open Full Size</span>
                </a>
                <button
                  type="button"
                  onClick={() => setSelectedSlipImage(null)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
                >
                  <X size={16} />
                </button>
              </div>
            </div>

            <div className="p-6 overflow-auto flex items-center justify-center bg-black/50 min-h-[300px]">
              <img
                src={selectedSlipImage}
                alt="Bank Deposit Slip Full"
                className="max-w-full max-h-[70vh] rounded-xl border border-white/10 object-contain shadow-2xl"
              />
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: REJECT SLIP WITH FEEDBACK ── */}
      {rejectModalTenant && (
        <div className="fixed inset-0 z-[999999] bg-black/80 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-[#0B101B] border border-rose-500/40 rounded-2xl w-full max-w-md overflow-hidden shadow-[0_0_60px_rgba(244,63,94,0.25)] animate-in fade-in zoom-in-95">
            <div className="px-6 py-4 border-b border-white/10 flex items-center justify-between bg-rose-950/40">
              <div className="flex items-center gap-2">
                <AlertCircle size={16} className="text-rose-400" />
                <h3 className="text-sm font-black text-white font-mono">
                  REJECT BANK SLIP
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setRejectModalTenant(null)}
                className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-white/10 transition cursor-pointer"
              >
                <X size={16} />
              </button>
            </div>

            <div className="p-6 space-y-4 text-xs">
              <p className="text-slate-300 font-sans">
                Please provide the reason why the deposit slip for <strong className="text-white">{rejectModalTenant.tenantName}</strong> was rejected. This feedback will be displayed to the tenant so they can correct and re-upload.
              </p>

              {/* Quick Preset Buttons */}
              <div className="space-y-1.5">
                <label className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                  Quick Select Common Reasons
                </label>
                <div className="space-y-1">
                  {[
                    'Slip image unreadable or cropped. Please re-upload a clear photo.',
                    'Transferred amount does not match package pricing.',
                    'Bank transaction reference number not found in company account statement.',
                    'Incorrect company bank account number.'
                  ].map((preset, pIdx) => (
                    <button
                      key={pIdx}
                      type="button"
                      onClick={() => setRejectReason(preset)}
                      className="w-full text-left p-2 rounded-lg bg-slate-900 border border-white/5 hover:border-rose-500/40 text-slate-300 hover:text-white text-[11px] transition cursor-pointer"
                    >
                      {preset}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-[10px] font-mono font-bold text-slate-400 uppercase">
                  Rejection Reason / Notes
                </label>
                <textarea
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Explain why the slip was rejected..."
                  className="w-full bg-slate-900 border border-white/10 rounded-xl p-3 text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-rose-400 font-sans resize-none"
                />
              </div>
            </div>

            <div className="px-6 py-4 border-t border-white/10 bg-slate-950/60 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setRejectModalTenant(null)}
                className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-xl text-xs font-mono transition cursor-pointer"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleRejectSlip}
                disabled={slipActionLoading === String(rejectModalTenant.tenantId)}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-500 text-white font-black rounded-xl text-xs font-mono transition shadow-lg flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                {slipActionLoading === String(rejectModalTenant.tenantId) && (
                  <RefreshCw size={13} className="animate-spin" />
                )}
                <span>Confirm Reject</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TenantBillingHub;
